import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Avatar, rupee, StatusPill } from "@/components/Bits";
import { CalendarDays, ChevronRight, ShieldCheck, ChevronDown, Pencil, User } from "lucide-react";

export default function CheckoutSummary() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [carer, setCarer] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [agree, setAgree] = useState(false);
  const [openCharges, setOpenCharges] = useState(true);

  useEffect(() => {
    api.get("/bookings").then((r) => {
      const b = r.data.find((x) => x.id === bookingId);
      setBooking(b || false);
      if (b) api.get(`/carers/${b.carer_id}`).then((c) => setCarer(c.data)).catch(() => {});
    });
    api.get("/care-profiles").then((r) => setProfiles(r.data)).catch(() => {});
  }, [bookingId]);

  if (booking === false) return <Shell><div className="bmc-wrap py-24 text-center">Booking not found. <Link to="/seeker" className="text-coral">Back to portal</Link></div></Shell>;
  if (!booking) return <Shell><div className="bmc-wrap py-24 text-center text-ink-soft">Loading…</div></Shell>;

  if (booking.payment_status === "Paid")
    return <Shell><div className="bmc-wrap py-24 text-center"><p className="text-lg font-bold">This booking is already paid ✅</p><Link to="/seeker" className="btn-pill bg-coral text-white px-6 py-3 mt-4 inline-flex">Go to Bookings</Link></div></Shell>;

  const recipient = profiles[0];

  return (
    <Shell>
      <div className="bmc-wrap py-6">
        <nav className="flex items-center gap-2 text-xs text-ink-faint mb-6">
          <Link to="/" className="hover:text-coral">Home</Link><ChevronRight size={12} />
          <Link to="/find-care" className="hover:text-coral">Find Care</Link><ChevronRight size={12} />
          <span className="text-coral font-semibold">Summary</span>
        </nav>

        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          {/* LEFT */}
          <div>
            <h1 className="text-2xl font-bold mb-5">Booking Details</h1>
            <div className="card-bmc p-5">
              <div className="flex gap-4">
                <Avatar initials={carer?.avatar_initials || booking.carer_name.split(" ").map((s) => s[0]).slice(0, 2).join("")} gradient={carer?.gradient} size={64} />
                <div className="flex-1">
                  <h3 className="text-lg font-bold">{booking.carer_name}</h3>
                  <p className="text-coral text-sm font-semibold">{booking.role}</p>
                  {carer && <p className="text-xs text-ink-soft mt-0.5"><span className="text-coral font-semibold">{carer.exp_years} YEARS</span> · {(carer.languages || []).join(", ")}</p>}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-line-2 flex flex-wrap items-center gap-4">
                <span className="text-[11px] font-bold tracking-wide bg-sage-soft text-sage-deep px-2.5 py-1 rounded">{(booking.channel || "APP").toUpperCase()} BOOKING</span>
                <span className="flex items-center gap-2 text-sm text-ink"><CalendarDays size={15} className="text-coral" /> {booking.date}</span>
                <span className="text-sm text-ink-soft">· {booking.shift}</span>
                <span className="text-sm text-ink-soft">· {booking.location}</span>
              </div>
            </div>

            <h2 className="text-xl font-bold mt-8 mb-4">Recipient Details</h2>
            <div className="card-bmc p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-butter-soft text-[#9A7320]"><User size={18} /></span>
                  <p className="font-semibold">{recipient ? `${recipient.name}` : user?.name}<span className="text-ink-soft font-normal text-sm ml-2">{recipient ? `${recipient.relationship}, ${recipient.age}` : ""}</span></p>
                </div>
                <button onClick={() => nav("/seeker")} className="text-sm text-coral border border-line rounded-full px-4 py-1.5 hover:border-coral" data-testid="change-recipient">Change</button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <p className="text-xs text-ink-soft mb-1.5">Contact number</p>
                  <div className="flex items-center border border-line rounded-r px-3 py-2.5 bg-ivory text-sm">
                    <span className="text-ink-soft mr-2">+91</span>{(user?.phone || "").replace("+91", "").trim() || "Add your number"}<Pencil size={13} className="ml-auto text-ink-faint" />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-ink-soft mb-1.5">Recipient name</p>
                  <div className="border border-line rounded-r px-3 py-2.5 bg-ivory text-sm">{recipient?.name || user?.name}</div>
                </div>
              </div>
              <p className="text-xs text-ink-faint bg-ivory-2 rounded-r px-3 py-2.5 mt-4">The carer will contact you on this number to coordinate the visit.</p>
            </div>
          </div>

          {/* RIGHT */}
          <div className="space-y-4 lg:sticky lg:top-6 self-start">
            <div className="rounded-r-lg p-5 text-white" style={{ background: "linear-gradient(120deg,#2A2420,#4a3f37)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/60">BookMyCarer Circle · save more</p>
                  <p className="font-display font-bold text-lg text-white">Members save ~₹1,200/mo</p>
                  <p className="text-butter font-bold mt-1">₹199 / 12 months</p>
                </div>
                <button className="btn-pill bg-white/15 text-white px-4 py-2 text-sm border border-white/20">+ Add</button>
              </div>
            </div>

            <div className="card-bmc overflow-hidden">
              <button onClick={() => setOpenCharges((v) => !v)} className="w-full flex items-center justify-between p-4 font-bold">
                Total Charges <ChevronDown size={16} className={openCharges ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {openCharges && (
                <div className="px-4 pb-4 space-y-2 text-sm">
                  <Row l="Service charge" v={rupee(booking.amount)} />
                  <Row l="Convenience fee" v="₹0" />
                  <Row l="GST (incl.)" v="Included" muted />
                  {booking.trust_guarantee && <Row l="Trust Guarantee" v="Added" />}
                </div>
              )}
            </div>

            <div className="card-bmc p-5">
              <div className="flex items-center justify-between mb-4">
                <div><p className="text-xs text-ink-soft">To Pay</p><p className="font-display font-bold text-2xl">{rupee(booking.amount)}</p></div>
                <button disabled={!agree} onClick={() => nav(`/checkout/${bookingId}/pay`)}
                  className="btn-pill bg-coral text-white px-7 py-3 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="pay-and-confirm">
                  Pay & Confirm
                </button>
              </div>
              <label className="flex items-start gap-2 text-xs text-ink-soft">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="accent-coral mt-0.5" data-testid="agree-terms" />
                I agree to the <span className="text-coral">Terms &amp; Conditions</span> and consent to be contacted for this booking.
              </label>
            </div>
            <p className="flex items-center justify-center gap-2 text-xs text-ink-faint"><ShieldCheck size={14} className="text-sage" /> 100% Secured Payments · Powered by Razorpay</p>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Row({ l, v, muted }) {
  return <div className="flex justify-between"><span className="text-ink-soft">{l}</span><span className={muted ? "text-ink-faint" : "font-semibold"}>{v}</span></div>;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ivory">
      <div className="h-[64px] bg-card border-b border-line flex items-center"><div className="bmc-wrap"><Logo /></div></div>
      {children}
    </div>
  );
}
